import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { matchPath, useLocation } from 'react-router';
import SmeConfigContext from 'data/SmeConfigContext';
import { MenuAction } from 'models/types';
import CssBaseline from '@mui/material/CssBaseline';
import FooterLayout from 'layouts/FooterLayout';
import SmeDrawer from 'containers/SmeDrawer';
import SmeAppBar from 'containers/SmeAppBar';
import { GetAllSections } from 'services/section';
import { SectionStubDto, SectionResponseDto, CurrentUserDto } from 'models/dtoTypes';
import { drawerWidth } from 'atlasConfig';
import { useSmeMenuList } from 'atlas/generateMenuList';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SmeApp';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  appBar: `${PREFIX}-appBar`,
  menuButton: `${PREFIX}-menuButton`,
  menuButtonHidden: `${PREFIX}-menuButtonHidden`,
  title: `${PREFIX}-title`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  drawerOpen: `${PREFIX}-drawerOpen`,
  content: `${PREFIX}-content`,
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
  fixedHeight: `${PREFIX}-fixedHeight`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    background: 'white',
    color: '#121212',
    paddingRight: 24, // keep right padding when drawer closed
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.menuButton}`]: {
    marginRight: 36,
  },

  [`& .${classes.menuButtonHidden}`]: {
    display: 'none',
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBarSpacer}`]: theme.mixins.toolbar,

  [`& .${classes.contentContainer}`]: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    marginLeft: 72,
  },

  [`& .${classes.drawerOpen}`]: {
    transitionDuration: '0.5s', // animation close/open
    marginLeft: drawerWidth,
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
    paddingBottom: 150,
  },

  [`& .${classes.container}`]: {
    position: 'relative',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },

  [`& .${classes.fixedHeight}`]: {
    height: 240,
  },
}));

interface Props {
  actions?: MenuAction[];
  children: React.ReactNode;
}

export default function SmeApp(props: Props): JSX.Element {
  const { actions } = props;
  const [open, setOpen] = useState(true);
  const [error, setError] = useState('');
  const [sections, setSections] = useState<SectionStubDto[]>([]);
  const [currentSectionResponse, setCurrentSectionResponse] = useState<
    SectionResponseDto | undefined
  >(undefined);
  const [currentUserDto, setCurrentUserDto] = useState<CurrentUserDto | undefined>(undefined);

  const { t } = useTranslation('default');

  const menuList = useSmeMenuList(sections);
  // avoid call setter when component is unmount
  const mountedRef = useRef(true);

  const location = useLocation<History>();
  const { pathname } = location;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const errorHandler = (error: { type: string; message: string }) => {
      if (mountedRef.current) {
        setError(error.message);
      }
    };
    const initialLoadData = async () => {
      // fetch all sections for sidebar items
      const response = await GetAllSections(undefined, errorHandler);
      if (response?.data && mountedRef.current) {
        setSections(response.data);
      }
    };
    void initialLoadData();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const renderError = () => {
    return <h1>{error}</h1>;
  };

  const getDashboardTitle = () => {
    const select = menuList.find((m) => matchPath(pathname, { path: m.path, exact: false }));
    if (select?.subItems) {
      const selectL2 = select.subItems.find((m) =>
        matchPath(pathname, { path: m.path, exact: true }),
      );
      return selectL2?.label || select?.label || '';
    }
    return select?.label || t('dashboard') || '';
  };

  const renderContent = () => {
    return (
      <SmeConfigContext.Provider
        value={{
          sections, // global access to get current section title
          setSections,
          currentSectionResponse, // global access company informations
          setCurrentSectionResponse,
          currentUserDto, // global access current user and current organistion info
          setCurrentUserDto,
        }}
      >
        {props.children}
      </SmeConfigContext.Provider>
    );
  };

  return (
    <Root className={classes.root}>
      <CssBaseline />
      <SmeAppBar
        open={open}
        title={getDashboardTitle()}
        actions={actions}
        onDrawerOpen={handleDrawerOpen}
      />
      <SmeDrawer open={open} onDrawerClose={handleDrawerClose} sections={sections} />
      <main className={clsx(classes.contentContainer, open && classes.drawerOpen)}>
        <div className={classes.appBarSpacer} />
        <div className={classes.content}>{error ? renderError() : renderContent()}</div>
        <FooterLayout />
      </main>
    </Root>
  );
}
